/**
 * PostHog Event Names Registry
 *
 * Convention: present tense snake_case (object_action)
 * See: https://posthog.com/docs/product-analytics/best-practices
 *
 * Use properties for context (source, platform, school_slug),
 * not separate event names.
 */
export const AnalyticsEvents = {
  // Content lifecycle
  CONTENT_CREATE: 'content_create',
  CONTENT_VIEW: 'content_view',
  CONTENT_ENGAGE: 'content_engage',
  CONTENT_DISMISS: 'content_dismiss',
  CONTENT_LIKE: 'content_like',
  ANNOUNCEMENT_CREATE: 'announcement_create',
  INBOUND_EMAIL_RECEIVE: 'inbound_email_receive',

  // Comments & reactions
  // Phase 2 may revisit the like/unlike pair shape; ships under working names
  // to close the over-count gap in net engagement metrics.
  COMMENT_ADD: 'comment_add',
  COMMENT_UPDATE: 'comment_update',
  COMMENT_LIKE: 'comment_like',
  COMMENT_UNLIKE: 'comment_unlike',
  CONTENT_UNLIKE: 'content_unlike',
  REPLY_ADD: 'reply_add',

  // User lifecycle
  USER_LOGIN: 'user_login',
  USER_FIRST_LOGIN: 'user_first_login',
  USER_ADD_TO_SCHOOL: 'user_add_to_school',
  USER_JOIN_SCHOOL: 'user_join_school',

  // Assignments
  ASSIGNMENT_CREATE: 'assignment_create',
  ASSIGNMENT_UPDATE: 'assignment_update',
  ASSIGNMENT_DUE_REMIND: 'assignment_due_remind',
  ASSIGNMENT_EVENT: 'assignment_event',

  // Journeys
  JOURNEY_STEP_COMPLETE: 'journey_step_complete',
  JOURNEY_COMPLETE: 'journey_complete',

  // Short URLs
  SHORT_URL_CREATE: 'short_url_create',
  SHORT_URL_CLICK: 'short_url_click',
  SHORT_URL_UPDATE: 'short_url_update',
  SHORT_URL_DEACTIVATE: 'short_url_deactivate',

  // Platform
  PLATFORM_SYNC_COMPLETE: 'platform_sync_complete',

  // OneSignal send-side (backend)
  PUSH_SENT: 'push_sent',
  SMS_SENT: 'sms_sent',
  EMAIL_SENT: 'email_sent',

  // Feed search (mobile) — Posts MVP. NEVER attach the raw query string as a
  // property (PII); only query_length / has_filters / counts / latency.
  FEED_SEARCH_SUBMITTED: 'feed_search_submitted',
  FEED_SEARCH_RESULT: 'feed_search_result',
  FEED_SEARCH_ZERO_RESULT: 'feed_search_zero_result',
  FEED_SEARCH_RESULT_TAPPED: 'feed_search_result_tapped',
  FEED_SEARCH_RECENT_TAPPED: 'feed_search_recent_tapped',

  // Chat (mobile)
  CHAT_MESSAGE_SEND: 'chat_message_send',
  CHAT_MESSAGE_DELETE: 'chat_message_delete',
  CHAT_CREATE: 'chat_create',
  CHAT_ARCHIVE: 'chat_archive',
  CHAT_UNARCHIVE: 'chat_unarchive',
  CHAT_SEARCH: 'chat_search',
  CHAT_REACTION_ADD: 'chat_reaction_add',

  // Directory (mobile)
  DIRECTORY_SEARCH: 'directory_search',
  DIRECTORY_PROFILE_VIEW: 'directory_profile_view',
  DIRECTORY_FILTER_APPLY: 'directory_filter_apply',

  // Calendar (mobile)
  CALENDAR_DATE_SELECT: 'calendar_date_select',
  CALENDAR_VIEW_MODE_CHANGE: 'calendar_view_mode_change',
  CALENDAR_EVENT_OPEN: 'calendar_event_open',
  CALENDAR_EVENT_SHARE: 'calendar_event_share',
  CALENDAR_EVENT_EXPORT: 'calendar_event_export',
  CALENDAR_FILTER_APPLY: 'calendar_filter_apply',

  // Schedules (mobile)
  SCHEDULE_DAY_SWITCH: 'schedule_day_switch',
  SCHEDULE_VIEW_MODE_CHANGE: 'schedule_view_mode_change',
  SCHEDULE_SECTION_TAP: 'schedule_section_tap',
  SCHEDULE_ATTENDANCE_OPEN: 'schedule_attendance_open',

  // Attendance writes (backend — plan 2026-08-07-001, U12/KTD10)
  //
  // The funnel used to end at SCHEDULE_ATTENDANCE_OPEN above, because the write
  // path emitted no product analytics at all — which is why "teachers opened the
  // screen and nothing happened" went unnoticed at Reich for a whole spring.
  // These two close it: one event when a submission is accepted for processing,
  // one carrying its real outcome.
  //
  // Properties carry `capability` (teacher | admin), `outcome`, the school's
  // three-state setting, dry-run, and counts. NEVER a student identifier, name,
  // or excuse description — the actor is not student PII, the roster is.
  ATTENDANCE_SUBMIT: 'attendance_submit',
  ATTENDANCE_SUBMIT_OUTCOME: 'attendance_submit_outcome',

  // Classes (mobile)
  // Fired after a class tap resolves, carrying `outcome` (entered | peek_fallback)
  // and, on a fallback, `fallback_reason` — otherwise direct entry degrades to the
  // class-peek silently and the only trace is a log line.
  CLASS_TAP: 'class_tap',

  // Media (mobile)
  MEDIA_UPLOAD_START: 'media_upload_start',
  MEDIA_UPLOAD_COMPLETE: 'media_upload_complete',
  MEDIA_ITEM_VIEW: 'media_item_view',
  MEDIA_DOWNLOAD: 'media_download',
  MEDIA_SHARE: 'media_share',
  COLLECTION_CREATE: 'collection_create',
  COLLECTION_VIEW: 'collection_view',

  // Groups (mobile)
  GROUP_POST_CREATE: 'group_post_create',
  GROUP_NOTIFICATION_CHANGE: 'group_notification_change',
  GROUP_MUTE_TOGGLE: 'group_mute_toggle',
  GROUP_SETTINGS_CHANGE: 'group_settings_change',
  GROUP_IDENTITY_CHANGE: 'group_identity_change',
  GROUP_LEAVE: 'group_leave',
  GROUP_MEMBERS_VIEW: 'group_members_view',

  // Notifications (mobile)
  PUSH_NOTIFICATION_TAP: 'push_notification_tap',
  PUSH_NOTIFICATION_RECEIVE: 'push_notification_receive',
  PUSH_NOTIFICATION_PARSE_FAILED: 'push_notification_parse_failed',
  PUSH_REGISTRATION_STATE: 'push_registration_state',
  NOTIFICATION_PERMISSION_REQUEST: 'notification_permission_request',
  NOTIFICATION_SOFT_ASK_SHOWN: 'notification_soft_ask_shown',
  NOTIFICATION_MARK_ALL_READ: 'notification_mark_all_read',
  DEEP_LINK_RESOLVED: 'deep_link_resolved',
  DEEP_LINK_FAILED: 'deep_link_failed',
  SCHOOL_SWITCH_TRIGGERED_BY_NOTIFICATION: 'school_switch_triggered_by_notification',
  NOTIFICATION_DROPPED: 'notification_dropped',

  // Detail pages (mobile)
  DETAIL_VIEW: 'detail_view',
  DETAIL_TAB_SWITCH: 'detail_tab_switch',
  DETAIL_ACTION_TAP: 'detail_action_tap'
} as const

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents]
